import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { memo } from 'react';
import { ProductThumbnail } from '../product-thumbnail';

export type PinnedVariantHeaderProps = {
  name: string;
  sku: string;
  imageUrl?: string | null;
  price: number;
  remain: number;
  isCreatingOrder: boolean;
  onEditPrice: () => void;
  onAdjustIn: () => void;
  onAdjustOut: () => void;
};

function PinnedVariantHeaderComponent({
  name,
  sku,
  imageUrl,
  price,
  remain,
  isCreatingOrder,
  onEditPrice,
  onAdjustIn,
  onAdjustOut,
}: PinnedVariantHeaderProps) {
  const stockTone =
    remain <= 0 ? 'red' : remain < 10 ? 'yellow' : 'green';

  const stockClass = {
    red: 'bg-red-100 border-red-300 text-red-700',
    yellow: 'bg-yellow-100 border-yellow-300 text-yellow-700',
    green: 'bg-green-100 border-green-300 text-green-700',
  }[stockTone];

  const stockValueClass = {
    red: 'text-red-600',
    yellow: 'text-yellow-600',
    green: 'text-green-600',
  }[stockTone];

  return (
    <div className="mb-2 shrink-0">
      <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-2.5 shadow-sm">
        {isCreatingOrder && (
          <div className="mb-2 flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1.5 text-sm text-blue-600">
            <span className="inline-flex h-3.5 w-3.5 items-center justify-center">
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            </span>
            <span className="font-medium">Creating order…</span>
          </div>
        )}

        <div className="flex items-start gap-2.5">
          <div className="shrink-0">
            <ProductThumbnail
              imageUrl={imageUrl}
              alt={name}
              size="md"
              borderStyle="white"
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-2">
              <h3 className="mb-1 truncate text-sm font-bold leading-tight text-slate-800">{name}</h3>
              <span className="inline-flex items-center rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                {sku}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="flex cursor-pointer items-center gap-1.5 rounded-md bg-gradient-to-br from-green-500 to-emerald-600 px-2.5 py-1.5 text-white shadow-sm transition-all hover:brightness-110 active:scale-95"
                onClick={onEditPrice}
                title="Click to edit price"
              >
                <span className="text-[10px] font-medium opacity-90">Price:</span>
                <span className="text-base font-bold">${price.toFixed(2)}</span>
              </button>

              <div className={cn('flex items-center gap-1.5 rounded border px-2.5 py-1.5', stockClass)}>
                <span className="text-[10px] font-semibold">Stock:</span>
                <span className={cn('text-base font-bold tabular-nums', stockValueClass)}>
                  {Math.max(0, remain)}
                </span>
              </div>

              <div className="ml-auto flex items-center gap-1">
                <Button
                  onClick={onAdjustIn}
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 rounded-full border-emerald-500 text-emerald-600 hover:border-emerald-600 hover:bg-emerald-50"
                  title="Add stock"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                </Button>
                <Button
                  onClick={onAdjustOut}
                  variant="outline"
                  size="icon"
                  className="h-7 w-7 rounded-full border-red-500 text-red-600 hover:border-red-600 hover:bg-red-50"
                  title="Remove stock"
                >
                  <MinusIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const PinnedVariantHeader = memo(PinnedVariantHeaderComponent);
